import type { ReactNode } from "react";

/** A control that is DESIGNED but NOT WIRED — disabled, and honest about it.
 *
 *  WHY THIS EXISTS. The Products and Organisations modules were ported from
 *  Fabric as stage A: real layout, real shapes, fixtures underneath and no
 *  backend. Their action buttons rendered as ordinary live buttons and did
 *  nothing at all on click. Dattatray found them one at a time — "Add spaces
 *  does nothing", "Update installer does nothing", "Add rule does nothing",
 *  "Edit map does nothing" — which is the correct response to a button that
 *  looks pressable and is not.
 *
 *  A dead control is worse than a missing one. A missing button says "this is
 *  not here yet"; a dead button says "this is here" and then lies, and the
 *  person is left wondering whether they mis-clicked, whether it failed
 *  silently, or whether something is broken. There were **39 of them** across
 *  seven files.
 *
 *  SAME WORD THE RAIL ALREADY USES. `Shell.tsx` renders an unwired module as a
 *  disabled nav item with a `SOON` chip. This is that convention applied one
 *  level down, so the console has one vocabulary for "designed, not wired"
 *  rather than two.
 *
 *  It is also GREPPABLE: `SoonButton` is the list of what still needs wiring,
 *  and each one turns into a real `<button onClick=…>` as its endpoint lands.
 */
export function SoonButton({ children, why, className = "btn ghost" }: {
  children: ReactNode;
  /** What will happen once it is wired — shown on hover. Say what the control
   *  DOES, not that it is disabled; the disabled state is already visible. */
  why?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={className}
      disabled
      // `title` rather than a chip beside every control: 39 inline SOON badges
      // would drown the pages this is meant to keep readable.
      title={why ? `Not wired yet - ${why}` : "Not wired yet"}
      style={{ opacity: 0.45, cursor: "not-allowed" }}
    >
      {children}
    </button>
  );
}

/** The page-level counterpart: one honest line at the top of a surface whose
 *  actions are all still fixtures.
 *
 *  Without it, a page of greyed-out buttons reads as "you lack permission",
 *  which is a different and more alarming message than "this is not built".
 */
export function SoonNotice({ what }: { what: string }) {
  return (
    <div className="panel panel-pad cap" style={{ marginBottom: 10 }}>
      <strong>Read-only for now.</strong> {what} The data shown here is
      representative, not live.
    </div>
  );
}
